import React, { useContext, useCallback, useState, useRef, useEffect, useMemo } from "react";
import { Bar } from "react-chartjs-2";
import "chart.js/auto";
import 'chartjs-adapter-moment';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Chart as ChartJS } from "chart.js";
import AuthContext from "../../../contexts/auth-context";

import { ChartHeader, ResolutionToggle, applyChartTheme, CHART_THEME } from "../../Concession/charts/chartTheme";
ChartJS.register(annotationPlugin);

// Function to convert game start time to annotation timestamp matching the chart's time system
const convertGameStartTimeToAnnotation = (gameDate, gameStartTime, baseDate) => {
  if (!gameDate || !gameStartTime || !baseDate) return null;
  
  const [hours, minutes] = gameStartTime.split(':').map(Number);
  
  // Create base date at midnight (same as chart's getIntervalKey)
  const chartBaseDate = new Date(baseDate);
  chartBaseDate.setHours(0, 0, 0, 0);
  
  // Create game start date with the same base date
  const gameStartDate = new Date(chartBaseDate);
  gameStartDate.setHours(hours, minutes, 0, 0);
  
  // Calculate minutes from base date (matching the chart's time system)
  const minutesFromBase = (gameStartDate.getTime() - chartBaseDate.getTime()) / 60000;
  
  // Create annotation timestamp using the same logic as getIntervalKey
  const annotationTime = new Date(chartBaseDate.getTime() + minutesFromBase * 60000);
  
  return annotationTime.toISOString();
};

const OccupancyChart = ({ data, currentLocation, dayType, currentGame }) => {
  const authCtx = useContext(AuthContext);
  const [dataValues, setDataValues] = useState([]);
  const [chartResolution, setChartResolution] = useState(1);
  const [loading, setLoading] = useState(true);
  const resolutions = [1, 5, 10, 15, 30, 60];
  const scrollContainerRef = useRef(null);

  const getIntervalKey = (incrementingValue, resolution) => {
    const baseDate = new Date(data[0].time2);
    baseDate.setHours(0, 0, 0, 0);
    const newDate = new Date(baseDate.getTime() + incrementingValue * 60000);
    newDate.setMinutes(Math.floor(newDate.getMinutes() / resolution) * resolution, 0, 0);
    return newDate.toISOString();
  };


  const updateDataMap = useCallback((resolution = chartResolution) => {
    const dataMap = new Map();

    data.forEach((item) => {
      if (item.time > 600) {
        const key = getIntervalKey(item.time, resolution);
        const entry = dataMap.get(key) || { totalPerc: 0, count: 0 };

        entry.totalPerc += item.perc;
        entry.count++;

        dataMap.set(key, { ...entry, dateTime: key });
      }
    });

    const aggregatedData = Array.from(dataMap.values()).map(
      ({ dateTime, totalPerc, count }) => ({
        dateTime,
        perc: Math.round(totalPerc / count),
      })
    );

    setDataValues(aggregatedData);
    setLoading(false); // Set loading to false when data is fully loaded
  }, [data, chartResolution]);

  const maxValue = Math.max(...dataValues.map((item) => item.perc));

  // Gets primary text color for chart
  const primaryTextColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-txt').trim();

  // Create game start annotation with useMemo - only for game days
  const gameStartAnnotation = useMemo(() => {
    if (dayType !== 'game') return null;
    return currentGame?.date && currentGame?.gameStartTime && data.length > 0 ? 
      convertGameStartTimeToAnnotation(currentGame.date, currentGame.gameStartTime, data[0].time2) : null;
  }, [dayType, currentGame?.date, currentGame?.gameStartTime, data]);

  const chartData = {
    labels: dataValues.map((item) => item.dateTime),
    datasets: [
      {
        label: `Avg. Occupancy`,
        data: dataValues.map((item) => item.perc),
        backgroundColor: dataValues.map((item) =>
          item.perc >= 100 ? "#d97706" : "#3b82f6"
        ),
        borderRadius: 2.5,
        barThickness: chartResolution,
      },
    ],
  };

  // Memoize chart options to prevent unnecessary recreations
  const mainChartOptions = useMemo(() => ({
    scales: {
      x: {
        type: "time",
        title: {
          display: true,
          text: "Time",
          color: primaryTextColor,
        },
        time: {
          unit: "hour",
          displayFormats: {
            hour: "h A",
          },
        },
        grid: {
          display: false,
        },
        ticks: {
          source: "auto",
          color: primaryTextColor,
          autoSkip: true,
          maxRotation: 0,
          minRotation: 0,
        },
      },
      y: {
        display: false, // Disable y-axis for main chart
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        callbacks: {
          label: (percent) => {
            return `${percent.raw}% | ${Math.round(percent.raw * currentLocation.maxOccupancy / 100)} of ${currentLocation.maxOccupancy}`
          },
        },
      },
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: primaryTextColor,
          usePointStyle: true,
          pointStyle: 'rectRounded',
          generateLabels: function(chart) {
            // Get default labels for the datasets
            const defaultLabels = ChartJS.defaults.plugins.legend.labels.generateLabels(chart);
            
            // Add game start indicator if we have one - put it FIRST
            if (gameStartAnnotation) {
              const gameStartLabel = {
                text: "Game Start",//authCtx.league === "mlb" ? "First Pitch" : authCtx.league === "nfl" ? "Kick Off" : "Game Start",
                fillStyle: 'transparent',
                strokeStyle: CHART_THEME.gameStart,
                lineWidth: 3,
                lineDash: [8, 2],
                pointStyle: 'line',
                hidden: false,
                index: -1 // Special index to identify this as non-clickable
              };
              
              // Put game start at the beginning
              return [gameStartLabel, ...defaultLabels];
            }
            
            return defaultLabels;
          }
        },
        onClick: function(evt, legendItem, legend) {
          // Only handle clicks for dataset items (not game start)
          if (legendItem.index !== -1) {
            // Use the default legend click behavior for dataset items
            ChartJS.defaults.plugins.legend.onClick.call(this, evt, legendItem, legend);
          }
          // Do nothing for game start (index === -1)
        }
      },
      annotation: {
        annotations: gameStartAnnotation ? [
          {
            type: 'line',
            mode: 'vertical',
            scaleID: 'x',
            value: gameStartAnnotation,
            borderColor: CHART_THEME.gameStart,
            borderWidth: 2,
            borderDash: [8, 4]
          }
        ] : []
      },
    },
  }), [gameStartAnnotation, primaryTextColor, currentLocation.maxOccupancy, authCtx.league]);

  const yAxisChartOptions = {
    scales: {
      x: {
        display: false, // Disable x-axis for y-axis chart
      },
      y: {
        display: true,
        position: 'left',
        title: {
          color: primaryTextColor,
          display: true,
          text: "Percentage",
        },
        grid: {
          display: false,
        },
        ticks: {
          color: primaryTextColor,
          precision: 0,
          beginAtZero: true,
          stepSize: 20,
          callback: function (value) {
            if (value % 20 === 0) {
              return value;
            }
          },
        },
        max: Math.max(Math.ceil(maxValue / 20) * 20),
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        enabled: false,
      },
      legend: {
        display: false,
      },
    },
  };

  useEffect(() => {
    setLoading(true); // Set loading true when data fetch starts
    updateDataMap();

    const scrollElement = scrollContainerRef.current;
    if (scrollElement) {
      const maxScrollLeft = scrollElement.scrollWidth - scrollElement.clientWidth;
      let start = null;

      const step = (timestamp) => {
        if (!start) start = timestamp;
        const progress = timestamp - start;
        const d = Math.min(progress / 2000, 1);
        scrollElement.scrollLeft = maxScrollLeft * d;

        if (progress < 2000) {
          requestAnimationFrame(step);
        }
      };

      requestAnimationFrame(step);
    }
  }, [updateDataMap]);

  if (!loading) {
    return (
      <div className="graph panel-2 cx-chart-card">
        <ChartHeader
          title="Average occupancy %"
          subtitle="Store occupancy as a share of its maximum. Amber bars are at or over capacity."
          controls={<ResolutionToggle resolutions={resolutions} value={chartResolution} onChange={(r) => { setChartResolution(r); updateDataMap(r); }} />}
        />
        <div style={{ 
          position: 'relative',
          height: '300px',
          marginTop: '8px',
        }}>
          <div
            ref={scrollContainerRef}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              marginLeft: '50px',
              overflowX: 'auto',
            }}
          >
            <div style={{
              width: `100%`,
              height: '100%'
            }}>
              <Bar 
                key={dayType}
                data={chartData} 
                options={applyChartTheme(mainChartOptions)} 
              />
            </div>
          </div>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100px',
            height: '258px',
            pointerEvents: 'none',
          }}>
            <Bar
              data={{...chartData, datasets: []}}
              options={applyChartTheme(yAxisChartOptions)}
            />
          </div>
        </div>
      </div>
    );
  };
}

export default OccupancyChart;